import { ChangeDetectionStrategy, Component, inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ITaskParameter } from '@fi-shared/fi-task-shared/fi-task-shared';
import { ITaskV2 } from '@fi-app/task-v2/fi-task-v2';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { MatSortDirection, ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { FiTaskV2Service } from '@fi-app/task-v2/fi-task-v2.service';
import { FiTaskCategoryType } from '@fi-app/task-v2/fi-task-v2-enums';
import { FiTaskStatusType } from '@fi-app/task-v2/fi-task-v2-enums';
import { FiTaskStatusesConfig } from '@fi-app/task-v2/fi-task-v2-consts';

@Component({
    selector: 'fi-tasks2',
    templateUrl: './tasks2.component.html',
    styleUrl: './tasks2.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-system-notifications-tasks2' },
    standalone: false,
})
export class SystemNotificationsTasks2Component extends FiRemoteStateFullDataSource<ITaskV2> implements OnDestroy {
    defaultSortColumnName: string = 'Deadline';
    defaultSortDirection = false;
    skipInitSelectedSort = true;
    itemsListType = FiItemsListType.SystemNotificationsTasks;
    objectType = ObjectTypes.Task2;
    //numberOfRecordsToTake = 10;

    protected readonly FiTaskStatusesConfig = FiTaskStatusesConfig;

    private readonly fiTaskV2HelperService = inject(FiTaskV2HelperService);
    private readonly fiTaskV2Service = inject(FiTaskV2Service);

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<ITaskV2>> {
        return new Promise<IItemsListDataFetchResult<ITaskV2>>((resolve, reject) => {
            const parameter = this.getParameter(loadArgs);
            this.fiTaskV2Service.getTasksListData(parameter).then((data) => {
                resolve(data);
            }, reject);
        });
    }

    private getParameter(loadArgs: ILoadDataArgs): ITaskParameter {
        const parameter = super.getItemsListParameter(loadArgs) as ITaskParameter;
        return this.fiTaskV2HelperService.getUpdatedTaskParameter(parameter, loadArgs, {
            filterType: SideListOptionType.TasksToBeDone,
            isArchived: false,
            //taskStatuses: this.fiTaskV2HelperService.getTaskStatusFilterByViewMode(this.viewMode),
        });
    }

    private _updatedItems: { task: ITaskV2; index: number }[] = [];
    protected completeSelectedTasks(): void {
        const selectedItems = this.selectedItems();
        this._updatedItems = selectedItems.map((item) => ({
            task: item,
            index: this.items().findIndex((t) => t.Id === item.Id),
        }));
        this.fiTaskV2Service
            .simpleUpdateTaskStatus(
                this._updatedItems.map((item) => ({ Id: item.task.Id, Status: FiTaskStatusType.Completed })),
                false,
            )
            .then(() => {
                this.removeData(selectedItems);
                this.showUndoSnackbar();
            });
    }

    private showUndoSnackbar(): void {
        const taskNames = this._updatedItems.map((item) => item.task.Name);
        this.fiTaskV2HelperService
            .showTaskStatusChangeSnackbar(taskNames.length > 1 ? taskNames : taskNames[0], FiTaskStatusType.Completed)
            .subscribe(() => {
                this.restorePreviousStatuses();
            });
    }

    private restorePreviousStatuses(): void {
        const restoreItemsData = this._updatedItems.map((item) => ({ Id: item.task.Id, Status: item.task.Status }));
        this.fiTaskV2Service.simpleUpdateTaskStatus(restoreItemsData, true).then(() => {
            const items = [...this.items()];
            this._updatedItems.forEach((item) => {
                items.splice(item.index, 0, item.task);
            });
            this.updateItems((items) => items);

            const taskNames = this._updatedItems.map((item) => item.task.Name);
            this.fiTaskV2HelperService.showTaskStatusChangeUndoneSnackbar(
                taskNames.length > 1 ? taskNames : taskNames[0],
                FiTaskStatusType.NotStarted,
            );
        });
    }

    protected getCategoryText(item: ITaskV2): string {
        // https://chainformation.atlassian.net/browse/FI-6609?focusedCommentId=65214 4c
        const category = item.Categories.includes(FiTaskCategoryType.AssignedToMe)
            ? FiTaskCategoryType.AssignedToMe
            : item.Categories[0];
        return this.fiTaskV2HelperService.getTaskCategoriesText([category]);
    }

    protected isTaskOverdue(item: ITaskV2): boolean {
        return item.IsDeadlinePassed && item.Status !== FiTaskStatusType.Completed;
    }
    protected getStatusColumnStartSortDirection() {
        // in process should be always on top at first click
        const notStartedText = FiTaskStatusesConfig[FiTaskStatusType.NotStarted].textId;
        const inProcessText = FiTaskStatusesConfig[FiTaskStatusType.InProcess].textId;
        return inProcessText < notStartedText ? MatSortDirection.Ascending : MatSortDirection.Descending;
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    protected readonly MatSortDirection = MatSortDirection;
}
